import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input, Textarea } from './ui/Input';
import { Select } from './ui/Select';
import { ExpenseEntry, SubItemConfig, Attachment, FormFieldConfig, MainCostCategory, Project } from '../types';
import { SUB_ITEM_FORM_CONFIG, COST_CATEGORIES, UNIT_OPTIONS, formatMonthForInput, formatMonthForDisplay, formatCurrency } from '../constants';
import { AttachmentInput } from './AttachmentInput';
import { useUsers } from '../hooks/useUsers';
import { useProjects } from '../hooks/useProjects';
import { useCompanies } from '../hooks/useCompanies';
import { TrashIcon } from './icons/TrashIcon';
import { PlusIcon } from './icons/PlusIcon';

interface DynamicEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  subItemId: string;
  obraId: string;
  categoryId?: string;
  existingExpense?: ExpenseEntry | null;
  allExpensesForSeries?: ExpenseEntry[];
  isLocked?: boolean;
}

interface SeriesInfo {
    baseDescription: string;
    firstMonth: string;
    lastMonth: string;
    totalInstallments: number;
    seriesExpenses: ExpenseEntry[];
}

// Helper functions for month string manipulation
const addMonthsToMonthString = (monthStr: string, monthsToAdd: number): string => {
    const [year, month] = monthStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, 15));
    date.setUTCMonth(date.getUTCMonth() + monthsToAdd);
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
};

const monthsBetween = (startStr: string, endStr: string): number => {
    const [startY, startM] = startStr.split('-').map(Number);
    const [endY, endM] = endStr.split('-').map(Number);
    return (endY - startY) * 12 + (endM - startM);
};

export const DynamicEntryModal: React.FC<DynamicEntryModalProps> = ({ isOpen, onClose, onSubmit, subItemId, obraId, categoryId, existingExpense, allExpensesForSeries = [], isLocked }) => {
  const { users } = useUsers();
  const { projects } = useProjects();
  const { companies } = useCompanies();
  const [formData, setFormData] = useState<Partial<ExpenseEntry>>({});
  const [config, setConfig] = useState<SubItemConfig | null>(null);

  // State for recurring series extension
  const [isEditingSeries, setIsEditingSeries] = useState(false);
  const [seriesInfo, setSeriesInfo] = useState<SeriesInfo | null>(null);
  const [newLastMonth, setNewLastMonth] = useState('');
  const [newUnitPriceForExtension, setNewUnitPriceForExtension] = useState<number | undefined>(undefined);

  // State for Rateio
  const [isRateioMode, setIsRateioMode] = useState(false);
  const [rateioTotalValue, setRateioTotalValue] = useState<number>(0);
  const [isRateioParcelado, setIsRateioParcelado] = useState(false);
  const [rateioAdditionalProjects, setRateioAdditionalProjects] = useState<string[]>([]);
  const [rateioMonths, setRateioMonths] = useState<{ month: string; percentages: { [projectId: string]: number } }[]>([
    { month: formatMonthForInput(undefined), percentages: { [obraId]: 50 } }
  ]);

  const isRateioEligible = subItemId.startsWith('cio_') || subItemId.startsWith('cdo_');

  const getProjectLabel = useCallback((project: Project) => {
      const company = companies.find(c => c.id === project.companyId);
      // Remove default city suffix if it exists in the name so we can format it nicely
      let baseName = project.name;
      if (company && baseName.includes(`${company.address.city}/${company.address.state}`)) {
          baseName = baseName.replace(` - ${company.address.city}/${company.address.state}`, '');
      }
      return company ? `${baseName} (Cidade: ${company.address.city} - ${company.address.state})` : project.name;
  }, [companies]);

  useEffect(() => {
    // Reset series and rateio state on open
    setIsEditingSeries(false);
    setSeriesInfo(null);
    setNewUnitPriceForExtension(undefined);
    setIsRateioMode(false);
    setRateioTotalValue(0);
    setIsRateioParcelado(false);
    setRateioAdditionalProjects([]);
    setRateioMonths([{ month: formatMonthForInput(undefined), percentages: { [obraId]: 100 } }]);

    const formConfig = SUB_ITEM_FORM_CONFIG[subItemId] || null;
    setConfig(formConfig);

    if (formConfig) {
        const initialData = formConfig.formFields.reduce((acc, field) => {
            (acc as any)[field.name] = '';
            return acc;
        }, {} as Partial<ExpenseEntry>);
        
        if (existingExpense) {
            let initialFormData = { ...initialData, ...existingExpense };

            // --- Rateio Detection ---
            if (existingExpense.rateioId && allExpensesForSeries) {
                const rateioMatch = existingExpense.description?.match(/^(.*) \(Rateio - \d+(?:\.\d+)?%\)$/);
                if (rateioMatch) {
                    initialFormData.description = rateioMatch[1].trim();
                }

                const relatedExpenses = allExpensesForSeries.filter(e => e.rateioId === existingExpense.rateioId);
                if (relatedExpenses.length > 0) {
                    setIsRateioMode(true);
                    
                    const monthsMap = new Map<string, { month: string; percentages: { [projectId: string]: number }; totalValue: number }>();
                    const additionalProjectIds = new Set<string>();

                    relatedExpenses.forEach(exp => {
                        const month = exp.month || '';
                        if (exp.projectId !== obraId) {
                            additionalProjectIds.add(exp.projectId);
                        }

                        const match = exp.description?.match(/\(Rateio - (\d+(?:\.\d+)?)%\)/);
                        const percent = match ? parseFloat(match[1]) : 0;

                        if (!monthsMap.has(month)) {
                            monthsMap.set(month, { month, percentages: {}, totalValue: 0 });
                        }
                        const monthData = monthsMap.get(month)!;
                        monthData.percentages[exp.projectId] = percent;
                        
                        if (percent > 0 && monthData.totalValue === 0) {
                            monthData.totalValue = exp.amount / (percent / 100);
                        }
                    });

                    const monthsArray = Array.from(monthsMap.values()).sort((a, b) => a.month.localeCompare(b.month));
                    setRateioMonths(monthsArray.map(m => ({ month: m.month, percentages: m.percentages })));
                    setRateioAdditionalProjects(Array.from(additionalProjectIds));
                    
                    if (monthsArray.length > 0) {
                        setRateioTotalValue(Math.round(monthsArray[0].totalValue));
                    }
                }
            } else {
                setRateioTotalValue(existingExpense.amount || 0);
            }

            // --- Recurring Series Detection ---
            const recurringMatch = existingExpense.description?.match(/^(.*) \(Parcela \d+\/\d+\)$/);
            if (recurringMatch) {
                const baseDescription = recurringMatch[1].trim();
                initialFormData.description = baseDescription;
                
                const seriesExpenses = allExpensesForSeries.filter(exp => 
                    exp.projectId === existingExpense.projectId &&
                    exp.subCategoryItemId === existingExpense.subCategoryItemId &&
                    exp.description?.startsWith(baseDescription) &&
                    exp.description?.match(/ \(Parcela \d+\/\d+\)$/)
                ).sort((a, b) => (a.month || '').localeCompare(b.month || ''));

                if (seriesExpenses.length > 0) {
                    const firstMonth = seriesExpenses[0].month!;
                    const lastMonth = seriesExpenses[seriesExpenses.length - 1].month!;
                    
                    setIsEditingSeries(true);
                    setSeriesInfo({
                        baseDescription,
                        firstMonth,
                        lastMonth,
                        totalInstallments: seriesExpenses.length,
                        seriesExpenses,
                    });
                    setNewLastMonth(lastMonth);

                    // Pre-populate rateioMonths from the series if not already in rateio mode
                    if (!existingExpense.rateioId) {
                        setRateioMonths(seriesExpenses.map(exp => ({
                            month: exp.month || '',
                            percentages: { [obraId]: 100 }
                        })));
                        setRateioTotalValue(seriesExpenses[0].amount || 0);
                    }
                }
            }
            
            // --- Normal Edit/Add Setup ---
            setFormData(initialFormData);
        } else {
            const defaultDate = new Date().toISOString().split('T')[0];
            const defaultMonth = formatMonthForInput(undefined);
            setFormData({ ...initialData, date: defaultDate, month: defaultMonth });
        }
    }
}, [subItemId, existingExpense, isOpen, allExpensesForSeries]);

  useEffect(() => {
    const qty = formData.quantity;
    const price = formData.unitPrice;
    if (typeof qty === 'number' && typeof price === 'number' && !isNaN(qty) && !isNaN(price)) {
      const calculatedAmount = qty * price;
      
      if (!isEditingSeries && !isRateioMode) {
        setFormData(prev => ({...prev, amount: calculatedAmount}));
      }
      
      // Sync rateioTotalValue with form's calculated amount if in rateio mode
      if (isRateioMode) {
        setRateioTotalValue(calculatedAmount);
      }
    }
  }, [formData.quantity, formData.unitPrice, isEditingSeries, isRateioMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
        const { checked } = e.target as HTMLInputElement;
        setFormData(prev => {
            const newState = { ...prev, [name]: checked };
            if (name === 'isRecurring' && !checked) {
                newState.recurrenceMonths = undefined; 
            }
            return newState;
        });
        return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'number' && value !== '' ? parseFloat(value) : value }));
  };

  const handleAttachmentChange = useCallback((attachments: Attachment[]) => {
      setFormData(prev => ({ ...prev, attachments }));
  }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // --- Handle Rateio Submission ---
        if (isRateioMode && isRateioEligible) {
            if (rateioAdditionalProjects.length === 0) {
                alert('Por favor, selecione pelo menos uma obra adicional para o rateio.');
                return;
            }
            if (rateioTotalValue <= 0) {
                alert('O valor total do rateio deve ser maior que zero.');
                return;
            }
            if (rateioMonths.length === 0) {
                alert('Adicione pelo menos um mês para o rateio.');
                return;
            }

            // Validate that each month sums to 100%
            for (const rm of rateioMonths) {
                const totalPercent = Object.values(rm.percentages).reduce((sum, p) => sum + (p || 0), 0);
                if (Math.abs(totalPercent - 100) > 0.01) {
                    alert(`A soma das porcentagens para o mês ${formatMonthForDisplay(rm.month)} deve ser 100% (atualmente ${totalPercent}%).`);
                    return;
                }
            }

            const creations: Partial<ExpenseEntry>[] = [];
            const deletions: string[] = [];
            const rateioId = existingExpense?.rateioId || `rateio_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            
            let mainCategory = categoryId as MainCostCategory;
            if (!mainCategory) {
                for (const cat of COST_CATEGORIES) {
                    if (cat.subItems.some(s => s.id === subItemId)) {
                        mainCategory = cat.id;
                        break;
                    }
                }
            }
            
            if (!mainCategory) {
                // Fallback to prefix if still not found
                mainCategory = subItemId.split('_')[0].toLowerCase() as MainCostCategory;
            }

            const subCategoryItemId = subItemId;
            
            // Find the sub-item name from COST_CATEGORIES
            let subCategoryItemName = subItemId;
            for (const cat of COST_CATEGORIES) {
                const sub = cat.subItems.find(s => s.id === subItemId);
                if (sub) {
                    subCategoryItemName = sub.name;
                    break;
                }
            }

            // If editing, delete old entries
            if (existingExpense) {
                if (existingExpense.rateioId && allExpensesForSeries) {
                    const oldEntries = allExpensesForSeries.filter(e => e.rateioId === existingExpense.rateioId);
                    deletions.push(...oldEntries.map(e => e.id));
                } else if (isEditingSeries && seriesInfo) {
                    // It was a series, delete all entries in the series to replace with rateio
                    deletions.push(...seriesInfo.seriesExpenses.map(e => e.id));
                } else {
                    // It was a normal entry, delete it to replace with rateio
                    deletions.push(existingExpense.id);
                }
            }

            const activeRateioTotalValue = isRateioParcelado && rateioMonths.length > 0 
                ? rateioTotalValue / rateioMonths.length 
                : rateioTotalValue;

            rateioMonths.forEach(rm => {
                const projectIds = [obraId, ...rateioAdditionalProjects];
                projectIds.forEach(pId => {
                    const percent = rm.percentages[pId] || 0;
                    if (percent <= 0) return;

                    const amount = activeRateioTotalValue * (percent / 100);

                    creations.push({
                        ...formData,
                        id: undefined, // Let backend generate new IDs
                        projectId: pId,
                        mainCategory,
                        subCategoryItemId,
                        subCategoryItemName,
                        month: rm.month,
                        date: `${rm.month}-01`,
                        amount,
                        description: `${formData.description || ''} (Rateio - ${percent}%)`.trim(),
                        rateioId,
                    });
                });
            });

            onSubmit({ updates: [], creations, deletions });
            return;
        }

        // --- Handle Normal Submission (potentially switching from Rateio) ---
        if (!isRateioMode && existingExpense?.rateioId && allExpensesForSeries) {
            const oldEntries = allExpensesForSeries.filter(e => e.rateioId === existingExpense.rateioId);
            const deletions = oldEntries.map(e => e.id);
            
            // Create a new normal entry from current form data
            const creations = [{
                ...formData,
                id: undefined,
                rateioId: undefined,
                projectId: obraId,
                mainCategory: subItemId.split('_')[0].toLowerCase() as MainCostCategory,
                subCategoryItemId: subItemId,
                subCategoryItemName: (function() {
                    for (const cat of COST_CATEGORIES) {
                        const sub = cat.subItems.find(s => s.id === subItemId);
                        if (sub) return sub.name;
                    }
                    return subItemId;
                })(),
            }];
            
            onSubmit({ updates: [], creations, deletions });
            return;
        }

    // --- Handle Series Extension/Update Submission ---
    if (isEditingSeries && seriesInfo) {
        const isExtending = newLastMonth > seriesInfo.lastMonth;
        
        if (newLastMonth < seriesInfo.lastMonth) {
            alert('A nova data final não pode ser anterior à data final atual da série.');
            return;
        }
        
        const monthsToAdd = isExtending ? monthsBetween(seriesInfo.lastMonth, newLastMonth) : 0;
        const newTotalInstallments = seriesInfo.totalInstallments + monthsToAdd;

        // Get the base description from the form, stripping any existing (Parcela X/Y)
        let baseDescription = formData.description || seriesInfo.baseDescription;
        baseDescription = baseDescription.replace(/ \(Parcela \d+\/\d+\)$/, '').trim();

        // 1. Prepare updates for existing expenses
        const updates = seriesInfo.seriesExpenses.map(exp => {
            const installmentMatch = exp.description?.match(/ \(Parcela (\d+)\/\d+\)$/);
            const currentInstallmentNumber = installmentMatch ? installmentMatch[1] : '?';
            
            // Merge formData into existing expense, but keep id, month, date, etc.
            const { id, month, date, createdAt, projectId, mainCategory, subCategoryItemId, subCategoryItemName, ...restOfForm } = formData;
            
            return {
                ...exp,
                ...restOfForm,
                description: `${baseDescription} (Parcela ${currentInstallmentNumber}/${newTotalInstallments})`,
            };
        });

        // 2. Prepare new expenses to be created
        const creations: Partial<ExpenseEntry>[] = [];
        if (isExtending) {
            const templateExpense = updates[0]; // Use updated first expense as template

            for (let i = 1; i <= monthsToAdd; i++) {
                const newMonth = addMonthsToMonthString(seriesInfo.lastMonth, i);
                const newInstallmentNumber = seriesInfo.totalInstallments + i;
                
                // Use newUnitPriceForExtension if provided, otherwise use the one from the form
                const unitPrice = newUnitPriceForExtension !== undefined ? newUnitPriceForExtension : templateExpense.unitPrice;
                const quantity = templateExpense.quantity || 1;
                const amount = (typeof unitPrice === 'number' && typeof quantity === 'number') ? unitPrice * quantity : templateExpense.amount;

                creations.push({
                    ...templateExpense,
                    id: undefined, // Let backend/context generate new ID
                    month: newMonth,
                    date: `${newMonth}-01`, // Set a default date for the new month
                    unitPrice,
                    amount,
                    description: `${baseDescription} (Parcela ${newInstallmentNumber}/${newTotalInstallments})`,
                });
            }
        }
        
        onSubmit({ updates, creations });
        return;
    }

    // --- Handle Normal Submission ---
    if (config) {
        for (const field of config.formFields) {
            if (field.required && !formData[field.name]) {
                alert(`O campo "${field.label}" é obrigatório.`);
                return;
            }
        }
    }
    const qty = formData.quantity;
    const price = formData.unitPrice;
    let finalData = {...formData};
    if (typeof qty === 'number' && typeof price === 'number') {
        finalData.amount = qty * price;
    }
    onSubmit(finalData);
  };
  
  if (!config) return null;

  const employeeOptions = [...users].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map(e => ({ value: e.id, label: e.name.toUpperCase() }));

  const title = isEditingSeries ? 'Editar Série de Lançamentos Recorrentes' : `${existingExpense ? 'Editar' : 'Adicionar'} Lançamento`;

  // --- RENDER LOGIC ---

  const renderField = (field: FormFieldConfig) => {
    const value = formData[field.name] as any;
    const gridSpan = field.gridCols ? `lg:col-span-${field.gridCols}` : 'lg:col-span-1';
    const finalGridSpan = field.gridCols === 3 ? 'md:col-span-2 lg:col-span-3' : gridSpan;

    switch (field.type) {
      case 'boolean': return (<div key={field.name} className={`flex items-center pt-5 ${finalGridSpan}`}><input id={field.name} name={field.name} type="checkbox" checked={!!value} onChange={handleChange} className="h-4 w-4 rounded border-dark-tertiary bg-dark-secondary text-brand-teal focus:ring-brand-teal" /><label htmlFor={field.name} className="ml-2 block text-sm font-medium text-dark-text-secondary">{field.label}</label></div>);
      case 'select_requester': return <Select key={field.name} label={field.label} name={field.name} value={value || ''} onChange={handleChange} options={employeeOptions} placeholderOption="Selecione um requisitante" required={field.required} containerClassName={finalGridSpan} />;
      case 'select_unit': return <Select key={field.name} label={field.label} name={field.name} value={value || ''} onChange={handleChange} options={UNIT_OPTIONS} placeholderOption="Selecione uma unidade" required={field.required} containerClassName={finalGridSpan} />;
      case 'textarea': return <Textarea key={field.name} label={field.label} name={field.name} value={value || ''} onChange={handleChange} required={field.required} containerClassName={`md:col-span-2 ${finalGridSpan}`} />;
      case 'attachments': return <AttachmentInput key={field.name} label={field.label} attachments={value || []} onChange={handleAttachmentChange} containerClassName={`md:col-span-2 ${finalGridSpan}`} />;
      case 'month': return <Input key={field.name} label={field.label} name={field.name} type="month" value={formatMonthForInput(value) || ''} onChange={handleChange} required={field.required} containerClassName={finalGridSpan} />;
      case 'amount': return <Input key={field.name} label={field.label} name={field.name} type="number" step="0.01" value={value || ''} onChange={handleChange} required={field.required} containerClassName={finalGridSpan} />;
      default: return <Input key={field.name} label={field.label} name={field.name} type={field.type} value={value || ''} onChange={handleChange} required={field.required} step={field.type === 'number' ? 'any' : undefined} containerClassName={finalGridSpan}/>;
    }
  };

  const renderFormFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {config.formFields.map(field => {
            if (field.name === 'recurrenceMonths' && !formData.isRecurring) return null;
            return renderField(field);
        })}
    </div>
  );

  const renderTotalAmount = () => (
    formData.quantity !== undefined && formData.unitPrice !== undefined && (
        <div className="p-3 bg-dark-surface rounded-md text-right mt-4">
            <span className="text-sm text-dark-text-secondary">Valor Total: </span>
            <span className="text-lg font-bold text-brand-lime">{formatCurrency(formData.amount || 0)}</span>
        </div>
    )
  );

  const handleAddRateioMonth = () => {
    const lastMonth = rateioMonths[rateioMonths.length - 1]?.month || formatMonthForInput(undefined);
    const nextMonth = addMonthsToMonthString(lastMonth, 1);
    
    // Default to 100% for current project, 0% for others for the new month
    const percentages: { [projectId: string]: number } = { [obraId]: 100 };
    rateioAdditionalProjects.forEach(pId => {
        percentages[pId] = 0;
    });
    
    setRateioMonths([...rateioMonths, { month: nextMonth, percentages }]);
  };

  const handleRemoveRateioMonth = (index: number) => {
    setRateioMonths(rateioMonths.filter((_, i) => i !== index));
  };

  const handleRateioMonthChange = (index: number, field: string, value: any) => {
    const newMonths = [...rateioMonths];
    (newMonths[index] as any)[field] = value;
    setRateioMonths(newMonths);
  };

  const handleRateioPercentageChange = (monthIndex: number, projectId: string, value: number) => {
    const newMonths = [...rateioMonths];
    newMonths[monthIndex].percentages = {
        ...newMonths[monthIndex].percentages,
        [projectId]: value
    };
    setRateioMonths(newMonths);
  };

  const handleAddRateioProject = () => {
    setRateioAdditionalProjects([...rateioAdditionalProjects, '']);
  };

  const handleRemoveRateioProject = (index: number) => {
    const projectToRemove = rateioAdditionalProjects[index];
    const newAdditional = rateioAdditionalProjects.filter((_, i) => i !== index);
    setRateioAdditionalProjects(newAdditional);
    
    // Remove from all months
    setRateioMonths(rateioMonths.map(rm => {
        const newPercentages = { ...rm.percentages };
        delete newPercentages[projectToRemove];
        return { ...rm, percentages: newPercentages };
    }));
  };

  const handleRateioProjectChange = (index: number, newProjectId: string) => {
    const oldProjectId = rateioAdditionalProjects[index];
    const newAdditional = [...rateioAdditionalProjects];
    newAdditional[index] = newProjectId;
    setRateioAdditionalProjects(newAdditional);
    
    // Update in all months
    setRateioMonths(rateioMonths.map(rm => {
        const newPercentages = { ...rm.percentages };
        if (oldProjectId) {
            const oldVal = newPercentages[oldProjectId];
            delete newPercentages[oldProjectId];
            newPercentages[newProjectId] = oldVal || 0;
        } else {
            newPercentages[newProjectId] = 0;
        }
        return { ...rm, percentages: newPercentages };
    }));
  };

  const activeRateioTotalValueUI = isRateioParcelado && rateioMonths.length > 0
      ? rateioTotalValue / rateioMonths.length
      : rateioTotalValue;

  if (isEditingSeries && seriesInfo) {
      return (
          <Modal isOpen={isOpen} onClose={onClose} title={title} size="2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                  <fieldset disabled={isLocked}>
                      {isRateioEligible && (
                          <div className="flex bg-dark-secondary p-1 rounded-lg mb-6">
                              <button
                                  type="button"
                                  onClick={() => setIsRateioMode(false)}
                                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${!isRateioMode ? 'bg-brand-teal text-white shadow-sm' : 'text-dark-text-secondary hover:text-dark-text-primary'}`}
                              >
                                  Lançamento Normal
                              </button>
                              <button
                                  type="button"
                                  onClick={() => setIsRateioMode(true)}
                                  className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${isRateioMode ? 'bg-brand-teal text-white shadow-sm' : 'text-dark-text-secondary hover:text-dark-text-primary'}`}
                              >
                                  Lançamento de Rateio
                              </button>
                          </div>
                      )}

                      {isRateioMode ? (
                          <div className="space-y-6">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-4">
                                      <Input 
                                          label="Valor Total do Rateio"
                                          type="number"
                                          step="0.01"
                                          value={rateioTotalValue || ''}
                                          onChange={(e) => setRateioTotalValue(parseFloat(e.target.value))}
                                          required
                                      />
                                      <div className="flex items-center">
                                          <input 
                                              type="checkbox" 
                                              id="isRateioParcelado1" 
                                              checked={isRateioParcelado} 
                                              onChange={(e) => setIsRateioParcelado(e.target.checked)} 
                                              className="mr-2 h-4 w-4 rounded border-dark-tertiary bg-dark-secondary text-brand-teal focus:ring-brand-teal"
                                          />
                                          <label htmlFor="isRateioParcelado1" className="text-sm font-medium text-dark-text-secondary">
                                              Parcelar valor total entre os meses
                                          </label>
                                      </div>
                                  </div>
                                  <div className="space-y-3">
                                      <div className="flex justify-between items-center">
                                          <label className="block text-xs font-medium text-dark-text-secondary uppercase tracking-wider">Obras Adicionais para Rateio</label>
                                          <Button type="button" variant="ghost" size="sm" onClick={handleAddRateioProject} className="text-brand-teal h-auto py-0">
                                              <PlusIcon className="h-4 w-4 mr-1" /> Adicionar Obra
                                          </Button>
                                      </div>
                                      <div className="space-y-2">
                                          {rateioAdditionalProjects.map((pId, pIdx) => (
                                              <div key={pIdx} className="flex items-center space-x-2">
                                                  <div className="flex-1">
                                                      <Select 
                                                          value={pId}
                                                          onChange={(e) => handleRateioProjectChange(pIdx, e.target.value)}
                                                          options={projects.filter(p => p.id !== obraId && (!rateioAdditionalProjects.includes(p.id) || p.id === pId)).map(p => ({ value: p.id, label: getProjectLabel(p) }))}
                                                          placeholderOption="Selecione a obra"
                                                          required
                                                      />
                                                  </div>
                                                  <button type="button" onClick={() => handleRemoveRateioProject(pIdx)} className="text-red-400 hover:text-red-300 p-1">
                                                      <TrashIcon className="h-4 w-4" />
                                                  </button>
                                              </div>
                                          ))}
                                          {rateioAdditionalProjects.length === 0 && (
                                              <p className="text-xs text-dark-text-secondary italic">Nenhuma obra adicional selecionada.</p>
                                          )}
                                      </div>
                                  </div>
                              </div>

                              <div className="space-y-4">
                                  <div className="flex justify-between items-center">
                                      <h4 className="text-sm font-semibold text-dark-text-primary uppercase tracking-wider">Distribuição por Mês</h4>
                                      <Button type="button" variant="ghost" size="sm" onClick={handleAddRateioMonth} className="text-brand-teal">
                                          + Adicionar Mês
                                      </Button>
                                  </div>
                                  
                                  {rateioMonths.map((rm, index) => {
                                      const totalPercent = Object.values(rm.percentages).reduce((sum, p) => sum + p, 0);
                                      const isTotalValid = Math.abs(totalPercent - 100) < 0.01;

                                      return (
                                          <div key={index} className={`p-4 bg-dark-surface rounded-lg border ${isTotalValid ? 'border-dark-tertiary' : 'border-red-500/50'} space-y-4`}>
                                              <div className="flex justify-between items-start">
                                                  <div className="w-1/3">
                                                      <Input 
                                                          label="Mês"
                                                          type="month"
                                                          value={rm.month}
                                                          onChange={(e) => handleRateioMonthChange(index, 'month', e.target.value)}
                                                          required
                                                      />
                                                  </div>
                                                  <div className="flex-1 ml-4 space-y-3">
                                                      <div className="grid grid-cols-2 gap-3">
                                                          <div>
                                                              <label className="block text-[10px] font-medium text-dark-text-secondary mb-1 truncate">
                                                                  {projects.find(p => p.id === obraId)?.name || 'Obra Atual'} (%)
                                                              </label>
                                                              <Input 
                                                                  type="number"
                                                                  value={rm.percentages[obraId] || 0}
                                                                  onChange={(e) => handleRateioPercentageChange(index, obraId, parseFloat(e.target.value))}
                                                                  min="0"
                                                                  max="100"
                                                                  required
                                                              />
                                                              <p className="text-[10px] text-brand-lime mt-1">
                                                                  {formatCurrency(activeRateioTotalValueUI * ((rm.percentages[obraId] || 0) / 100))}
                                                              </p>
                                                          </div>
                                                          {rateioAdditionalProjects.map(pId => (
                                                              <div key={pId}>
                                                                  <label className="block text-[10px] font-medium text-dark-text-secondary mb-1 truncate">
                                                                      {projects.find(p => p.id === pId)?.name || 'Obra Rateio'} (%)
                                                                  </label>
                                                                  <Input 
                                                                      type="number"
                                                                      value={rm.percentages[pId] || 0}
                                                                      onChange={(e) => handleRateioPercentageChange(index, pId, parseFloat(e.target.value))}
                                                                      min="0"
                                                                      max="100"
                                                                      required
                                                                  />
                                                                  <p className="text-[10px] text-brand-lime mt-1">
                                                                      {formatCurrency(activeRateioTotalValueUI * ((rm.percentages[pId] || 0) / 100))}
                                                                  </p>
                                                              </div>
                                                          ))}
                                                      </div>
                                                      <div className="flex justify-between items-center pt-2 border-t border-dark-tertiary">
                                                          <span className={`text-[10px] font-bold ${isTotalValid ? 'text-brand-lime' : 'text-red-400'}`}>
                                                              Total: {totalPercent.toFixed(1)}%
                                                          </span>
                                                          {!isTotalValid && (
                                                              <span className="text-[10px] text-red-400 italic">A soma deve ser 100%</span>
                                                          )}
                                                      </div>
                                                  </div>
                                                  <button type="button" onClick={() => handleRemoveRateioMonth(index)} className="text-red-400 hover:text-red-300 p-1 ml-2">
                                                      <TrashIcon className="h-5 w-5" />
                                                  </button>
                                              </div>
                                          </div>
                                      );
                                  })}
                              </div>
                          </div>
                      ) : (
                          <>
                              <div className='mb-6 p-4 bg-dark-surface rounded-lg border border-dark-tertiary space-y-2'>
                                  <p className='text-sm text-dark-text-secondary'>
                                    <strong className='text-dark-text-primary'>Informações da Série:</strong> {seriesInfo.totalInstallments} parcelas, de {formatMonthForDisplay(seriesInfo.firstMonth)} a {formatMonthForDisplay(seriesInfo.lastMonth)}.
                                  </p>
                                  <p className='text-xs text-gray-500 dark:text-dark-text-secondary'>
                                    Nota: Alterações nos campos abaixo serão aplicadas a <strong>todas</strong> as parcelas da série.
                                  </p>
                              </div>

                              {renderFormFields()}
                              {renderTotalAmount()}

                              <div className="mt-8 pt-6 border-t border-dark-tertiary">
                                  <h4 className="text-sm font-semibold text-dark-text-primary mb-4 uppercase tracking-wider">Extensão da Série (Opcional)</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-dashed border-dark-tertiary rounded-lg">
                                      <Input 
                                          label="Estender recorrência até o mês de:"
                                          type="month"
                                          value={newLastMonth}
                                          onChange={(e) => setNewLastMonth(e.target.value)}
                                          min={seriesInfo.lastMonth}
                                          containerClassName='mb-0'
                                      />
                                      <Input 
                                          label="Novo valor unitário para extensão:"
                                          type="number"
                                          step="0.01"
                                          value={newUnitPriceForExtension || ''}
                                          onChange={(e) => setNewUnitPriceForExtension(e.target.value !== '' ? parseFloat(e.target.value) : undefined)}
                                          placeholder="Opcional (mantém o atual se vazio)"
                                          containerClassName='mb-0'
                                      />
                                  </div>
                                  {newUnitPriceForExtension !== undefined && formData.quantity !== undefined && (
                                      <div className="mt-2 text-right text-xs text-brand-lime font-medium">
                                        Novo valor total por parcela estendida: {formatCurrency(newUnitPriceForExtension * (formData.quantity || 1))}
                                      </div>
                                  )}
                              </div>
                          </>
                      )}
                  </fieldset>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-dark-tertiary mt-6">
                      <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
                      <Button type="submit" variant="primary" disabled={isLocked}>
                        {isLocked ? 'Obra Bloqueada' : 'Salvar Alterações na Série'}
                      </Button>
                  </div>
              </form>
          </Modal>
      );
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="2xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        <fieldset disabled={isLocked}>
            {isRateioEligible && (
                <div className="flex bg-dark-secondary p-1 rounded-lg mb-6">
                    <button
                        type="button"
                        onClick={() => setIsRateioMode(false)}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${!isRateioMode ? 'bg-brand-teal text-white shadow-sm' : 'text-dark-text-secondary hover:text-dark-text-primary'}`}
                    >
                        Lançamento Normal
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsRateioMode(true)}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${isRateioMode ? 'bg-brand-teal text-white shadow-sm' : 'text-dark-text-secondary hover:text-dark-text-primary'}`}
                    >
                        Lançamento de Rateio
                    </button>
                </div>
            )}

            {isRateioMode ? (
                <div className="space-y-6">
                    <div className="space-y-4">
                        <Input 
                            label="Valor Total do Rateio"
                            type="number"
                            step="0.01"
                            value={rateioTotalValue || ''}
                            onChange={(e) => setRateioTotalValue(parseFloat(e.target.value))}
                            required
                        />
                        <div className="flex items-center">
                            <input 
                                type="checkbox" 
                                id="isRateioParcelado2" 
                                checked={isRateioParcelado} 
                                onChange={(e) => setIsRateioParcelado(e.target.checked)} 
                                className="mr-2 h-4 w-4 rounded border-dark-tertiary bg-dark-secondary text-brand-teal focus:ring-brand-teal"
                            />
                            <label htmlFor="isRateioParcelado2" className="text-sm font-medium text-dark-text-secondary">
                                Parcelar valor total entre os meses
                            </label>
                        </div>
                        
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <label className="block text-sm font-medium text-dark-text-primary">Obras Adicionais para Rateio</label>
                                <Button type="button" variant="ghost" size="sm" onClick={handleAddRateioProject} className="text-brand-teal">
                                    <PlusIcon className="h-4 w-4 mr-1" /> Adicionar Obra
                                </Button>
                            </div>
                            
                            {rateioAdditionalProjects.map((pId, idx) => (
                                <div key={idx} className="flex items-center space-x-2">
                                    <div className="flex-1">
                                        <Select 
                                            value={pId}
                                            onChange={(e) => handleRateioProjectChange(idx, e.target.value)}
                                            options={projects.filter(p => p.id !== obraId && (!rateioAdditionalProjects.includes(p.id) || p.id === pId)).map(p => ({ value: p.id, label: getProjectLabel(p) }))}
                                            placeholderOption="Selecione a obra"
                                            required
                                            containerClassName="mb-0"
                                        />
                                    </div>
                                    <button type="button" onClick={() => handleRemoveRateioProject(idx)} className="text-red-400 hover:text-red-300 p-2">
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                            {rateioAdditionalProjects.length === 0 && (
                                <p className="text-xs text-dark-text-secondary italic">Nenhuma obra adicional selecionada.</p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h4 className="text-sm font-semibold text-dark-text-primary uppercase tracking-wider">Distribuição por Mês</h4>
                            <Button type="button" variant="ghost" size="sm" onClick={handleAddRateioMonth} className="text-brand-teal">
                                + Adicionar Mês
                            </Button>
                        </div>
                        
                        {rateioMonths.map((rm, index) => (
                            <div key={index} className="p-4 bg-dark-surface rounded-lg border border-dark-tertiary space-y-4">
                                <div className="flex justify-between items-start">
                                    <div className="w-1/3">
                                        <Input 
                                            label="Mês"
                                            type="month"
                                            value={rm.month}
                                            onChange={(e) => handleRateioMonthChange(index, 'month', e.target.value)}
                                            containerClassName="mb-0"
                                            required
                                        />
                                    </div>
                                    <button type="button" onClick={() => handleRemoveRateioMonth(index)} className="text-red-400 hover:text-red-300 p-1">
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-4 items-end">
                                        <div>
                                            <label className="block text-xs font-medium text-dark-text-secondary mb-1">Obra Atual (%)</label>
                                            <Input 
                                                type="number"
                                                value={rm.percentages[obraId] || 0}
                                                onChange={(e) => handleRateioPercentageChange(index, obraId, parseFloat(e.target.value))}
                                                min="0"
                                                max="100"
                                                required
                                                containerClassName="mb-0"
                                            />
                                        </div>
                                        <div className="text-right text-xs text-dark-text-secondary font-medium pb-2">
                                            Valor: <span className="text-brand-lime">{formatCurrency(activeRateioTotalValueUI * ((rm.percentages[obraId] || 0) / 100))}</span>
                                        </div>
                                    </div>
                                    
                                    {rateioAdditionalProjects.map(pId => {
                                        const project = projects.find(p => p.id === pId);
                                        if (!project) return null;
                                        return (
                                            <div key={pId} className="grid grid-cols-2 gap-4 items-end">
                                                <div>
                                                    <label className="block text-xs font-medium text-dark-text-secondary mb-1">{project.name} (%)</label>
                                                    <Input 
                                                        type="number"
                                                        value={rm.percentages[pId] || 0}
                                                        onChange={(e) => handleRateioPercentageChange(index, pId, parseFloat(e.target.value))}
                                                        min="0"
                                                        max="100"
                                                        required
                                                        containerClassName="mb-0"
                                                    />
                                                </div>
                                                <div className="text-right text-xs text-dark-text-secondary font-medium pb-2">
                                                    Valor: <span className="text-brand-lime">{formatCurrency(activeRateioTotalValueUI * ((rm.percentages[pId] || 0) / 100))}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    <div className="pt-2 border-t border-dark-tertiary flex justify-between items-center">
                                        <span className="text-xs font-semibold text-dark-text-primary">Total:</span>
                                        <span className={`text-xs font-bold ${Math.abs(Object.values(rm.percentages).reduce((s, p) => s + (p || 0), 0) - 100) < 0.01 ? 'text-brand-lime' : 'text-red-400'}`}>
                                            {Object.values(rm.percentages).reduce((s, p) => s + (p || 0), 0)}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="pt-4 border-t border-dark-tertiary">
                        <h4 className="text-sm font-semibold text-dark-text-primary mb-4 uppercase tracking-wider">Dados Gerais do Lançamento</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {config.formFields.filter(f => !['amount', 'month', 'date', 'isRecurring', 'recurrenceMonths'].includes(f.name as string)).map(field => renderField(field))}
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {renderFormFields()}
                    {renderTotalAmount()}
                </>
            )}
        </fieldset>

        <div className="flex justify-end space-x-3 pt-4 border-t border-dark-tertiary mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={isLocked}>
            {isLocked ? 'Obra Bloqueada' : (existingExpense ? 'Salvar Alterações' : 'Adicionar Lançamento')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};